import httpClient from "../http/mainServerClient";
import { get, getAxiosByShopType, post } from "../http/platformServer";
import { jushitaAxios, pddAxios } from "../http/platformAxios";
import ShopDTO from "@/dto/shop/ShopDTO";
import ShopSettingDTO from "@/dto/shop/ShopSettingDTO";
import ShopType from "@/enum/ShopType";
import AppVersion from "@/enum/AppVersion";
import { PremiumFunctionType } from "../PremiumValidator";
import { WrapperList } from "@/utils/utilityTypes";
import mainServerClient from "../http/mainServerClient";

function getShopTypeAxios(shopType) {
    switch (shopType) {
        case "TAOBAO":
            return jushitaAxios;
        case "PINDUODUO":
            return pddAxios;
        default:
            return jushitaAxios;
    }
}

function sortShops(shops) {
    if (Array.isArray(shops)) {
        // index从小排到大
        shops.sort((a, b) => a.index - b.index);
    }

    return shops;
}

interface ShopOAuth {
    oauthUrl: string;
}

// 列出当前用户被允许查看的店铺
export const listAllowed = (): Promise<ShopDTO[]> =>
    httpClient.get("/shop/listAllowed").then((shops) => sortShops(shops));

// 列出当前用户所在公司的所有店铺
export const list = (): Promise<ShopDTO[]> => httpClient.get("/shop/list").then((shops) => sortShops(shops));

export const updateAllowedShop = (
    shopId: number,
    allowedShopIds: number[],
    privacyAllowedShopId: ShopDTO["privacyAllowedShopId"],
) => {
    return httpClient.post("/shop/update_allowed_shop", {
        shopId,
        allowedShopIds,
        privacyAllowedShopId,
    });
};

export const createShop = (name, type) =>
    httpClient.post<ShopDTO>(
        "/shop/create",
        { name, type },
        {
            pathPremiumValidator() {
                if (type === ShopType.MANUAL) {
                    return {
                        type: PremiumFunctionType.MANUAL_ORDER_SHOP_NUMBER_2,
                    };
                }

                return null;
            },
        },
    );

// 开启订单自动退款
export const enableAutoRefund = (enabled, shopId) => post(shopId, "/shop/enableAutoRefund", { enabled, shopId });

// 开启订单自动同步
export const enabledAutoSyncOrder = (enabled, shopId) => post(shopId, "/shop/enableAutoSyncOrder", { enabled, shopId });

//开启订单自动合并
export const enabledOrderMerge = (enabled, shopId) => post(shopId, "/shop/enableOrderAutoMerge", { enabled, shopId });

// 开启同步订单发票信息
export const enabledSyncInvoiceInfo = (enabled, shopId) =>
    httpClient.post("/shop/enableSyncInvoiceInfo", { enabled, shopId });

export const enableSyncOrderItemDiscounts = (enabled: boolean, shopId: number) =>
    post(shopId, "/shop/enableSyncOrderItemDiscounts", { enabled, shopId });

/**
 * 订单轨迹追踪 目前只有抖店才有
 */
export function enableOrderTrace(shopId: number, enabled: boolean) {
    return post<{ successful: boolean }>(shopId, "/shop/enableOrderTrace", {
        shopId,
        enabled,
    });
}

// 更新店铺排序Shop.index
export const updateIndex = (index, shopId) => httpClient.post(`/shop/updateIndex?index=${index}&shopId=${shopId}`);

/** 获取店铺相关信息 该接口在响应白名单中 */
export const getShopDetail = (shopId) => get<ShopSettingDTO>(shopId, "/shop/get", { id: shopId });

// 获取店铺对外开放的code
export const getShopCode = (shopId) => post<string>(shopId, "/shop/get_code", { shopId });

// 更新店铺shop,name,index
export const updateShop = ({ index, name, shopId, url }) =>
    httpClient.post(`/shop/updateShop`, { index, name, shopId, url });

const getAddPlatformShopForm = (shopType: ShopType): Promise<{ fields: any; authType: string }> => {
    const axios = getAxiosByShopType(shopType);
    return axios.get(`/shop/add_platform_shop/form?shopType=${shopType}`);
};

interface BuildAddPlatformShopOAuthUrlParams {
    formValues?: object;
    shopType: string;
    token: string;
    redirectUrl: string;
}

const buildAddPlatformShopOAuthUrl = ({
    formValues,
    shopType,
    token,
    redirectUrl,
}: BuildAddPlatformShopOAuthUrlParams): Promise<ShopOAuth> => {
    const axios = getShopTypeAxios(shopType);
    const formValuesJson = encodeURI(JSON.stringify(formValues));
    let url = encodeURI(redirectUrl);

    return axios.get(
        `/shop/add_platform_shop/oauth/url?shopType=${shopType}&token=${token}&formValuesJson=${formValuesJson}&redirectUrl=${url}`,
    );
};

// 重新授权
export const reAuth = (shopId, token, redirectUrl: string): Promise<{ oauthUrl: string }> =>
    get(shopId, "/shop/reauth", { shopId, token, redirectUrl });

/**
 * 批量获取店铺信息 （有一个店铺无访问权限，会整个异常）
 */
export function batchGetShop(shopIds: number[]) {
    const shopId = shopIds[0];

    return post<ShopSettingDTO[]>(shopId, "/shop/batchGet", shopIds);
}

/**批量获取店铺名称 */
export function batchGetShopName(selfShopId: number, shopIds: number[]) {
    return post<{ id: number; name: string }[]>(selfShopId, "/shop/batchGetShopName", shopIds);
}

/**删除店铺 */
export function removeShop(shopId: number) {
    return httpClient.post("/shop/cancel", {
        shopId,
    });
}

interface ShopSubscription {
    /**
     * 注意这个字段和Shop里面的subscriptionEndTime不一样
     */
    subscribedEndTime: string;
    appVersion: AppVersion;
}

/**
 * 这里返回的数据
 */
export function syncShopSubscription(shopId: number): Promise<ShopSubscription> {
    return post(shopId, "/app_subscription/validity/sync", { shopId });
}

/**
 * @deprecated
 */
export function syncShopSubscriptionByNode(shopId: number): Promise<ShopSettingDTO> {
    return get(shopId, "/shop/syncShopSubscription", { shopId });
}

/**转移主店铺 */
export function transferMainShop(newMasterShopId: number): Promise<void> {
    return httpClient.post("/shop/master/transfer", { newMasterShopId });
}

export function updateShippingDeadlineWarnHours(shopId, shippingDeadlineWarnHours): Promise<void> {
    return post(shopId, "/shop/updateShippingDeadlineWarnHours", { shopId, shippingDeadlineWarnHours });
}

/**白名单 */
/**通过关联code获取店铺信息 */
export function validAssociationCode(code: string): Promise<ShopDTO> {
    return httpClient.get("/shop/valid_association_code", { code });
}

/**生成店铺关联code，时效为30分钟，使用过后就会直接失效，无法重复使用  */
export function generateAssociationCode(shopId: number): Promise<string> {
    return httpClient.get("/shop/association_code", { shopId });
}

/**生成店铺关联code，时效为30分钟，使用过后就会直接失效，无法重复使用  */
export function generateLhBindingCode(shopId: number): Promise<string> {
    return httpClient.get("/shop/lh_binding_code", { shopId });
}

/**通过关联code关联与code对应的店铺 */
export function associateShop(code: string): Promise<void> {
    return httpClient.post<void>("/shop/associate", { code });
}

export enum PddAssociateResultEnum {
    /** Company内无PDD时与PDD关联 */
    "ASSOCIATE_SUCCESS" = "ASSOCIATE_SUCCESS",
    /** Company内有PDD时与PDD关联 */
    "PDD_INVITE_SUCCESS" = "PDD_INVITE_SUCCESS",
}

/**
 * 拼多多店铺发起关联请求
 */
export function pddAssociateShop(code: string): Promise<PddAssociateResultEnum> {
    return httpClient.post("/shop/associate_pdd", { code });
}

interface PddAssociateInvitation {
    inviterShopName: string;
    associateUrl: string;
}

/** 如果当前登录的用户是拼多多店铺时则调用，产看是否有用户邀请关联 */
export function getPddAssociateInvitation(): Promise<PddAssociateInvitation> {
    return httpClient.post("/shop/pdd_associate_invitation");
}

export function addPddFdsShop(pddShopId: number): Promise<ShopDTO> {
    return post(
        pddShopId,
        "/shop/add_pdd_fds_shop",
        { pddShopId },
        {
            pathPremiumValidator() {
                return {
                    shopId: pddShopId,
                    type: PremiumFunctionType.ADD_PDD_FDS_SHOP,
                };
            },
        },
    );
}

export function addPddFulfillment(pddShopId: number): Promise<ShopDTO> {
    return post(
        pddShopId,
        "/shop/add_pdd_fulfillment_shop",
        { pddShopId },
        {
            pathPremiumValidator() {
                return {
                    shopId: pddShopId,
                    type: PremiumFunctionType.ADD_PDD_FULFILLMENT_SHOP,
                };
            },
        },
    );
}

export function addTbDistributionShop(tbShopId: number): Promise<ShopDTO> {
    return post(tbShopId, "/shop/add_tb_distribution_shop", { tbShopId });
}

export function addJdPurchaseShop(shopId: number): Promise<ShopDTO> {
    return post(shopId, "/shop/add_jd_purchase_shop", { shopId });
}

export function addAliSupplierShop(shopId: number): Promise<ShopDTO> {
    return post(shopId, "/shop/add_alibaba_supplier_shop", { shopId });
}

export interface ShopAdvancedSettingDto {
    shopId: number;

    enabledPushCustomizationInfo?: boolean;

    hideCarInfo?: boolean;

    appendExtraWaybillNoToSellerRemarks?: boolean;

    enabledTopSellingProductPrinting?: boolean;

    enabledAutoMergeByTimeRange?: boolean;

    enabledAutoMergeBySameProduct?: boolean;

    autoMergeTimeRange?: string;

    /** 发货后多少个小时内订单需要进行异常检测，默认24小时 */
    abnormalCheckHoursAfterOrderShipped?: number;

    /** 是否启用异常订单弹窗通知，值不为false时默认需要弹窗提示 */
    enabledAbnormalOrderNotification?: boolean;

    /** 是否启自动识别属性中的颜色尺码相关内容进行排序，开启后计算订单的商品属性排序值的算法会有差异 */
    enabledOrderItemColorSizeSorting?: boolean;

    /**
     *
     * 排除自动合并的SKU信息，多个SKU用英文分号分隔，如果订单商品项包含其中一个sku时则不自动合并，格式：productId1:skuId1;productId1:skuId2;productId2:skuId2
     */
    excludedAutoMergeSkus?: string;
    /**
     * 排除自动合并的SKU数量
     * 不持久化，仅用于前端展示
     */
    excludedAutoMergeSkusCount?: number;

    /**
     * 最大可合并订单数量，抖店店铺默认为200，非抖店店铺默认为50
     */
    maxAutoMergeOrderCount?: number;

    /** 为null时默认为相同买家+相同收货人时才会合并 */
    orderAutoMergeRule?: OrderAutoMergeRule;

    /** 是否启用预售/全款预售订单仅与预售/全款预售订单自动合并 */
    enabledAutoMergePresaleWithPresaleOnly?: boolean;
}

/**
 * 获取店铺的高级设置
 */
export function getShopAdvancedSetting(shopId: number) {
    return post<ShopAdvancedSettingDto>(shopId, "/shop_advanced_setting", { shopId });
}

/**
 * 设置获取单号回传到卖家备注中
 */
export function appendExtraWaybillNoToSellerRemarks(shopId: number, enabled: boolean) {
    return post<ShopAdvancedSettingDto>(shopId, "/shop_advanced_setting/appendExtraWaybillNoToSellerRemarks", {
        shopId,
        enabled,
    });
}

/** 设置爆款模式 */
export function enabledTopSellingProductPrinting(shopId: number, enabled: boolean) {
    return post<ShopAdvancedSettingDto>(
        shopId,
        "/shop_advanced_setting/enabledTopSellingProductPrinting",
        {
            enabled,
            shopId,
        },
        {
            pathPremiumValidator() {
                return {
                    shopId,
                    type: PremiumFunctionType.TOP_SELLING_PRODUCT_PRINTING,
                };
            },
        },
    );
}

/**
 * 设置隐藏车型
 */
export function saveShopHideCarInfo(shopId: number, enabled: boolean) {
    return post<ShopAdvancedSettingDto>(shopId, "/shop_advanced_setting/setHideCarInfo", {
        shopId,
        enabled,
    });
}

/** 订单自动合并设置 */
export function saveAutoMergeByTimeRange(
    shopId: number,
    enabledAutoMergeByTimeRange: boolean,
    autoMergeTimeRange: string,
) {
    return post<ShopAdvancedSettingDto>(shopId, "/shop_advanced_setting/enabled_auto_merge_by_time_range", {
        shopId,
        enabledAutoMergeByTimeRange,
        autoMergeTimeRange,
    });
}

/**
 * 设置推送定制信息开关
 */
export function saveEnabledPushCustomizationInfo(shopId: number, enabled: boolean) {
    return post<ShopAdvancedSettingDto>(shopId, "/shop_advanced_setting/enabledPushCustomizationInfo", {
        shopId,
        enabled,
    });
}

/** 订单自动合并设置 */
export function saveAutoMergeBySameProduct(shopId: number, enabled: boolean) {
    return post<ShopAdvancedSettingDto>(shopId, "/shop_advanced_setting/enabled_auto_merge_by_same_product", {
        shopId,
        enabled,
    });
}
/** 『预售/全款预售订单仅和预售/全款预售订单自动合并』 设置 */
export function saveAutoMergePresaleWithPresaleOnly(shopId: number, enabled: boolean) {
    return post<ShopAdvancedSettingDto>(shopId, "/shop_advanced_setting/enabled_auto_merge_presale_with_presale_only", {
        shopId,
        enabled,
    });
}

export interface ShopAppVersionInfo {
    id: number;
    appVersion: AppVersion;
}

interface MigrateManualShopParams {
    migratingManualShopId: number;
    newOwnerShopId: number;
}

export function migrateManualShop(params: MigrateManualShopParams): Promise<ShopDTO> {
    return httpClient.post("/shop/migrate_manual_shop", params);
}

enum OperationType {
    /** 订单自动合并 */
    ORDER_AUTO_MERGE = "ORDER_AUTO_MERGE",
    /** 自动同步订单 */
    AUTO_SYNC_ORDER = "AUTO_SYNC_ORDER",
    /** 打印多个单号自动追加到备注 */
    APPEND_EXTRA_WAYBILL_NO_TO_SELLER_REMARKS = "APPEND_EXTRA_WAYBILL_NO_TO_SELLER_REMARKS",
    /** 爆款打单模式 */
    TOP_SELLING_PRODUCT_PRINTING = "TOP_SELLING_PRODUCT_PRINTING",
    /** 商品车型信息 */
    HIDE_CAR_INFO = "HIDE_CAR_INFO",
    /** 关联店铺 */
    ASSOCIATE_SHOP = "ASSOCIATE_SHOP",
    /** 取消关联 */
    CANCEL_ASSOCIATE_SHOP = "CANCEL_ASSOCIATE_SHOP",
    /** 指定最后发货时间提醒 */
    SHIPPING_DEADLINE_WARN_HOURS = "SHIPPING_DEADLINE_WARN_HOURS",
    /** 订单自动合并时间段 */
    AUTO_MERGE_RANGE_TIME = "AUTO_MERGE_RANGE_TIME",
    /** 订单轨迹 */
    ORDER_TRACE = "ORDER_TRACE",
    /** 同步订单商品项的优惠信息 (阿里巴巴优惠信息) */
    SYNC_ORDER_ITEM_DISCOUNTS = "SYNC_ORDER_ITEM_DISCOUNTS",
    /** 预发货自动发货 */
    PRE_SHIPPING_ORDER_AUTO_SHI = "PRE_SHIPPING_ORDER_AUTO_SHIP",
    /** 自动提单 */
    AUTO_PUSH_ORDER_TO_SUPPLIER = "AUTO_PUSH_ORDER_TO_SUPPLIER",
}

export interface ShopLog {
    shopId: number;
    operation: string;
    operator: string;
    dateCreated: string;
    operationType: OperationType;
}

export function getShopLog(shopId: number, pageNo, pageSize): Promise<WrapperList<ShopLog>> {
    return post(shopId, "/shop_log/list", { shopId, pageNo, pageSize });
}

/** 检查店铺是否有“待履约”的高级版 */
export function getPddHasPendingPremiumVersion(shopId): Promise<{ hasPendingPremiumVersion: boolean }> {
    return get(shopId, "/pdd_subscription_upgrade/has_pending_premium_version", { shopId });
}

/** 创建一个拼多多订购升级记录 */
export function createPddUpgradeRecord(shopId: number): Promise<void> {
    return post(shopId, `/pdd_subscription_upgrade/create_upgrade_record?shopId=${shopId}`);
}

export function createShopRenewRecord(params): Promise<void> {
    return mainServerClient.post("/shop_renewal_record", params, { hideModal: true });
}

/** 启用“有异常订单时打印发货页面自动弹窗提醒” */
export function enabledAbnormalOrderNotification(shopId: number, enabled: boolean) {
    return post<ShopAdvancedSettingDto>(shopId, "/shop_advanced_setting/enabled_abnormal_order_notification", {
        shopId,
        enabled,
    });
}

/** 设置发货后多少小时内检测异常订单 */
export function setAbnormalCheckHoursAfterOrderShipped(shopId: number, hours: number) {
    return post<ShopAdvancedSettingDto>(shopId, "/shop_advanced_setting/set_abnormal_check_hours_after_order_shipped", {
        shopId,
        hours,
    });
}

/** 启用“商品属性自动识别颜色尺码计算排序值” */
export function setOrderItemColorSizeSorting(shopId: number, enabled: boolean) {
    return post<ShopAdvancedSettingDto>(shopId, "/shop_advanced_setting/enabled_order_item_color_size_sorting", {
        shopId,
        enabled,
    });
}

export interface UpdateExcludedAutoMergeSkusParams {
    /**
     * 不可自动合并的SKU信息列表，只需回传productOriginalId和skuOriginalId
     */
    excludedAutoMergeSkus?: ExcludedAutoMergeSku[];
    /**
     * 店铺ID
     */
    shopId?: number;
}

export interface ExcludedAutoMergeSku {
    /**
     * 图片
     * 优先使用sku图片，如果sku没有图片则使用商品图片
     */
    picture?: string;
    /**
     * 商品平台ID
     */
    productOriginalId: string;
    /**
     * 商品标题
     */
    productTitle: string;
    /**
     * 店铺ID
     */
    shopId: number;
    /**
     * SKU平台ID
     */
    skuOriginalId?: string;
    /**
     * SKU属性
     */
    skuProperties?: string;
}

/** 设置不自动合并的sku */
export function updateExcludedAutoMergeSkus(params: UpdateExcludedAutoMergeSkusParams) {
    return post(params.shopId, "/shop_advanced_setting/update_excluded_auto_merge_skus", params);
}

/** 获取已设置的不自动合并sku列表 */
export function getExcludedAutoMergeSkus(shopId: number) {
    return get<ExcludedAutoMergeSku[]>(shopId, "/shop_advanced_setting/list_excluded_auto_merge_skus", {
        shopId,
    });
}

/**  设置自动合并订单数量上限 */
export function updateMaxAutoMergeOrderCount(shopId: number, maxAutoMergeOrderCount: number) {
    return post<ShopAdvancedSettingDto>(shopId, "/shop_advanced_setting/update_max_auto_merge_order_count", {
        shopId,
        maxAutoMergeOrderCount,
    });
}

/**
 * 订单自动合并规则；
 */
export enum OrderAutoMergeRule {
    /** 相同买家 */
    SameBuyerAndConsignee = "SAME_BUYER_AND_CONSIGNEE",
    /** 相同收货人不同买家 */
    SameConsignee = "SAME_CONSIGNEE",
}

/** 设置订单自动合并规则 */
export function updateOrderAutoMergeRule(shopId: number, orderAutoMergeRule: OrderAutoMergeRule) {
    return post<ShopAdvancedSettingDto>(shopId, "/shop_advanced_setting/update_order_auto_merge_rule", {
        shopId,
        orderAutoMergeRule,
    });
}

export default {
    listAllowed,
    list,
    createShop,
    enableAutoRefund,
    enabledAutoSyncOrder,
    enabledOrderMerge,
    enabledSyncInvoiceInfo,
    updateIndex,
    getShopDetail,
    updateShop,
    getAddPlatformShopForm,
    buildAddPlatformShopOAuthUrl,
    batchGetShopName,
    reAuth,
    syncShopSubscription,
};
